import type { U3500DocumentTreeNode } from '../../u3500GedDocumentSearch/U3500Service';

/** Tags de type dérivés des segments de chemin NAS (gabarit EPIC-0016). */
export const U3121_DOCUMENT_TYPE_TAGS = [
  'Index',
  'Plans',
  'Actes',
  'Infolot',
  'Historique',
  'Administration',
  'Terrain',
  'Analyse',
  'Documentations',
] as const;

export type U3121DocumentTypeTag = (typeof U3121_DOCUMENT_TYPE_TAGS)[number];

/** Identifiant spécial pour les fichiers hors gabarit (chip « Autre »). */
export const U3121_DOCUMENT_TYPE_TAG_OTHER = 'Autre';

export type U3121FlatDocumentItem = {
  documentId: string;
  name: string;
  fileName: string | null;
  relativePath: string;
  previewable: boolean;
  typeTags: U3121DocumentTypeTag[];
};

export type U3121DocumentNameMatchMode = 'contains' | 'startswith' | 'endswith';

export type U3121FlatDocumentFilter = {
  nameQuery?: string;
  /** Mode de correspondance du nom (défaut: contains). Comparaison hors extension. */
  nameMatchMode?: U3121DocumentNameMatchMode;
  /** Tags sélectionnés (union OR). Inclut éventuellement `U3121_DOCUMENT_TYPE_TAG_OTHER`. */
  selectedTags?: string[];
};

const KNOWN_TAG_BY_LOWER = new Map<string, U3121DocumentTypeTag>(
  U3121_DOCUMENT_TYPE_TAGS.map((tag) => [tag.toLowerCase(), tag])
);

/**
 * Retire la dernière extension (ex. `plan-a.pdf` → `plan-a`).
 * Pas d’effet si pas d’extension ou fichier caché (`.env`).
 */
export function u3121FileNameStem(fileName: string): string {
  const base = fileName.trim();
  const lastDot = base.lastIndexOf('.');
  if (lastDot <= 0) {
    return base;
  }
  return base.slice(0, lastDot);
}

/**
 * Compare un radical de nom (sans extension) selon startswith / contains / endswith.
 */
export function u3121MatchFileNameStem(
  stem: string,
  query: string,
  mode: U3121DocumentNameMatchMode
): boolean {
  const s = stem.toLowerCase();
  const q = query.toLowerCase();
  if (!q) {
    return true;
  }
  if (mode === 'startswith') {
    return s.startsWith(q);
  }
  if (mode === 'endswith') {
    return s.endsWith(q);
  }
  return s.includes(q);
}

/**
 * Dérive les tags de type depuis un chemin relatif (segments `/` ou `\`).
 * Insensible à la casse ; retourne les libellés canoniques du gabarit.
 */
export function u3121DeriveTypeTagsFromPath(relativePath: string): U3121DocumentTypeTag[] {
  if (!relativePath?.trim()) {
    return [];
  }
  const segments = relativePath.split(/[/\\]+/).filter(Boolean);
  const found = new Set<U3121DocumentTypeTag>();
  for (const segment of segments) {
    const known = KNOWN_TAG_BY_LOWER.get(segment.toLowerCase());
    if (known) {
      found.add(known);
    }
  }
  return U3121_DOCUMENT_TYPE_TAGS.filter((tag) => found.has(tag));
}

/**
 * Aplatit l’arbre documents : uniquement les nœuds fichier avec `documentId`.
 */
export function u3121FlattenDocumentTree(nodes: U3500DocumentTreeNode[]): U3121FlatDocumentItem[] {
  const items: U3121FlatDocumentItem[] = [];

  const walk = (nodeList: U3500DocumentTreeNode[]) => {
    for (const node of nodeList) {
      if (node.kind === 'file' && node.documentId) {
        items.push({
          documentId: node.documentId,
          name: node.name,
          fileName: node.fileName ?? null,
          relativePath: node.relativePath,
          previewable: node.previewable === true,
          typeTags: u3121DeriveTypeTagsFromPath(node.relativePath),
        });
      }
      if (node.children?.length) {
        walk(node.children);
      }
    }
  };

  walk(nodes);
  return items;
}

/**
 * Filtre la liste plate : nom (startswith / contains / endswith, hors extension, casse ignorée)
 * + tags en union (OR). Aucun tag sélectionné = pas de filtre type.
 */
export function u3121FilterFlatDocuments(
  items: U3121FlatDocumentItem[],
  filter: U3121FlatDocumentFilter
): U3121FlatDocumentItem[] {
  const rawQuery = filter.nameQuery?.trim() ?? '';
  const query = rawQuery ? u3121FileNameStem(rawQuery).toLowerCase() : '';
  const mode: U3121DocumentNameMatchMode = filter.nameMatchMode ?? 'contains';
  const selected = filter.selectedTags?.filter(Boolean) ?? [];
  const includeOther = selected.includes(U3121_DOCUMENT_TYPE_TAG_OTHER);
  const selectedKnown = selected.filter((t) => t !== U3121_DOCUMENT_TYPE_TAG_OTHER);

  return items.filter((item) => {
    if (query) {
      const stem = u3121FileNameStem(item.fileName || item.name);
      if (!u3121MatchFileNameStem(stem, query, mode)) {
        return false;
      }
    }

    if (selected.length === 0) {
      return true;
    }

    const hasKnownMatch = selectedKnown.some((tag) =>
      item.typeTags.some((t) => t.toLowerCase() === tag.toLowerCase())
    );
    const isOther = item.typeTags.length === 0;

    return hasKnownMatch || (includeOther && isOther);
  });
}

/**
 * Tags présents dans la liste (ordre gabarit) + « Autre » si au moins un fichier sans tag.
 */
export function u3121CollectAvailableTypeTags(items: U3121FlatDocumentItem[]): string[] {
  const present = new Set<U3121DocumentTypeTag>();
  let hasOther = false;
  for (const item of items) {
    if (item.typeTags.length === 0) {
      hasOther = true;
    } else {
      for (const tag of item.typeTags) {
        present.add(tag);
      }
    }
  }
  const tags: string[] = U3121_DOCUMENT_TYPE_TAGS.filter((tag) => present.has(tag));
  if (hasOther) {
    tags.push(U3121_DOCUMENT_TYPE_TAG_OTHER);
  }
  return tags;
}

/**
 * Affichage liste : retire le préfixe `{projetNumber}/` (ou `\`) du chemin relatif.
 */
export function u3121StripProjetNumberPrefix(
  relativePath: string,
  projetNumber: string | null | undefined
): string {
  const path = relativePath?.trim() ?? '';
  const num = projetNumber?.trim();
  if (!path || !num) {
    return path;
  }
  const escaped = num.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return path.replace(new RegExp(`^${escaped}[/\\\\]+`, 'i'), '');
}
